import React, { useState } from 'react';
import { Button } from '@fiverr-private/button';
import { ArrowRightIcon } from '@fiverr-private/visuals';
import { translate } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import {
    FederatedSeller,
    fetchCustomOrderFormData,
    useMonitoring,
    useSellerData,
} from '@fiverr-private/seller_pages_toolbox';
import { triggerSignUp } from '@fiverr-private/identification_triggers';
import pathfinder from '@fiverr-private/pathfinder';
import { PAGE_NAME } from '../../BIContext/events/consts';
import { useCustomOrderModalOpen } from '../hooks/useCustomOrderModal';
import { useContactMeProfessionBIExtensionData } from '../../BIContext';
import { useSelectedRole } from '../../../hooks/useSelectedRole';

const tryToSignUp = async () => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        await triggerSignUp({ biEnrichmentParams: { source: PAGE_NAME } });
    } catch (error) {
        window.location.assign(pathfinder('join'));
    }
};

interface CustomOrderButtonProps {
    fullWidth?: boolean;
    onClick?: () => void;
    buttonText?: string;
}

export const CustomOrderButton = ({
    fullWidth,
    onClick,
    buttonText = 'seller_page_perseus.professions_page.main_content.services_offered.get_quote',
}: CustomOrderButtonProps) => {
    const { userId } = getContext();
    const [isLoading, setIsLoading] = useState(false);
    const openModal = useCustomOrderModalOpen();
    const { fireEvents, Providers } = useMonitoring();
    const roleBiEnrichment = useContactMeProfessionBIExtensionData();
    const selectedRole = useSelectedRole();

    const { username } = useSellerData(selectSellerSlice);

    const handleOnClick = async () => {
        onClick?.();
        fireEvents({
            [Providers.BigQuery]: {
                type: 'clicked_on_get_a_quote ',
                page: {
                    element: {
                        name: 'role_get_a_quote',
                    },
                },
                ...roleBiEnrichment,
            },
        });

        if (!userId) {
            await tryToSignUp();
            return;
        }

        setIsLoading(true);

        try {
            const { data } = await fetchCustomOrderFormData(username, {
                gigSlug: selectedRole?.automationGig?.slug,
                includeHidden: true,
            });
            openModal(data);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Button size="lg" disabled={isLoading} loading={isLoading} onClick={handleOnClick} fullWidth={fullWidth}>
            {translate(buttonText)}
            <ArrowRightIcon />
        </Button>
    );
};

const selectSellerSlice = (seller: FederatedSeller) => ({
    username: seller.user.name,
});
